/**
 * Centralized date/time utilities for a UK-only restaurant.
 *
 * Rules of the codebase:
 *  - The database stores dates as UTC ISO 8601 strings (`...Z`) — they are
 *    absolute instants, not wall-clock times.
 *  - Anything user-facing (UI, emails, Telegram) and any "is it now / today?"
 *    comparison must be evaluated against `Europe/London`, never against the
 *    server's host timezone or the browser's local timezone.
 *  - Wall-clock strings on the wire (`YYYY-MM-DD`, `HH:MM`) are always
 *    interpreted as Europe/London by the server.
 *
 * Built-in `Intl.DateTimeFormat` only — no external deps.
 */

export const LONDON_TZ = 'Europe/London';

// ---------------------------------------------------------------------------
// Internal: derive a Date's components as seen in Europe/London.
// ---------------------------------------------------------------------------

interface LondonParts {
    year: number;
    month: number; // 1-12
    day: number;   // 1-31
    hour: number;  // 0-23
    minute: number;
    second: number;
}

const londonPartsFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
});

function getLondonParts(date: Date): LondonParts {
    const parts = londonPartsFormatter.formatToParts(date);
    const lookup: Record<string, string> = {};
    for (const p of parts) {
        if (p.type !== 'literal') lookup[p.type] = p.value;
    }
    return {
        year: Number(lookup.year),
        month: Number(lookup.month),
        day: Number(lookup.day),
        // `en-GB` with hour12:false can return "24" at midnight in some
        // engines; normalize to 0.
        hour: Number(lookup.hour) % 24,
        minute: Number(lookup.minute),
        second: Number(lookup.second),
    };
}

function pad2(n: number): string {
    return n < 10 ? `0${n}` : String(n);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * London calendar date key `YYYY-MM-DD` for the given instant (defaults to
 * "now"). DST-safe.
 */
export function londonDateKey(date: Date = new Date()): string {
    const p = getLondonParts(date);
    return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/**
 * London wall-clock time `HH:MM` for the given instant.
 */
export function londonTimeKey(date: Date = new Date()): string {
    const p = getLondonParts(date);
    return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * London hours/minutes (and minutes-since-midnight) for the given instant.
 */
export function londonHourMinute(date: Date = new Date()): {
    hours: number;
    minutes: number;
    minutesSinceMidnight: number;
} {
    const p = getLondonParts(date);
    return {
        hours: p.hour,
        minutes: p.minute,
        minutesSinceMidnight: p.hour * 60 + p.minute,
    };
}

/**
 * Snapshot of "now" in Europe/London.
 *  - `date`    : `YYYY-MM-DD`
 *  - `time`    : `HH:MM`
 *  - `minutes` : minutes since midnight London
 *  - `iso`     : the underlying UTC ISO string (absolute instant)
 */
export function nowLondon(now: Date = new Date()): {
    date: string;
    time: string;
    minutes: number;
    iso: string;
} {
    const p = getLondonParts(now);
    return {
        date: `${p.year}-${pad2(p.month)}-${pad2(p.day)}`,
        time: `${pad2(p.hour)}:${pad2(p.minute)}`,
        minutes: p.hour * 60 + p.minute,
        iso: now.toISOString(),
    };
}

/**
 * Add a number of calendar days to a London date key and return the new key.
 * Pure string-domain arithmetic — DST-safe (DST never changes the calendar
 * day count).
 */
export function addDaysLondon(dateKey: string, days: number): string {
    const [y, m, d] = dateKey.split('-').map(Number);
    // Use UTC midnight as a stable anchor; we only care about the calendar.
    const t = Date.UTC(y, m - 1, d) + days * 86400000;
    const dt = new Date(t);
    return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

/**
 * Convert a London wall-clock date+time (e.g. user-selected `2025-07-15`
 * `14:30`) into a UTC ISO instant, correctly accounting for BST/GMT.
 *
 * Algorithm: Pick an arbitrary UTC instant matching the wall-clock components,
 * measure London's offset at that instant, then correct. One iteration is
 * sufficient outside DST transitions; we do a second pass to stabilise on
 * boundary days.
 *
 * Edge cases:
 *  - **Spring-forward gap** (e.g. `2025-03-30 01:30` does not exist): the
 *    function returns the instant that the requested wall clock would have
 *    been had the jump not occurred (i.e. it resolves to the "after" side).
 *  - **Fall-back overlap** (e.g. `2025-10-26 01:30` happens twice): returns
 *    the **earlier** instant (BST side).
 */
export function londonWallClockToUTC(dateKey: string, time: string): string {
    const [y, m, d] = dateKey.split('-').map(Number);
    const [hh, mm] = time.split(':').map(Number);

    // Naive UTC instant matching the wall clock numerically.
    let guess = Date.UTC(y, m - 1, d, hh, mm, 0, 0);

    for (let i = 0; i < 2; i++) {
        const parts = getLondonParts(new Date(guess));
        const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        const offsetMs = asUTC - guess; // +3600000 in BST, 0 in GMT
        const corrected = Date.UTC(y, m - 1, d, hh, mm, 0, 0) - offsetMs;
        if (corrected === guess) break;
        guess = corrected;
    }

    return new Date(guess).toISOString();
}

/**
 * Format any instant (Date or ISO string) in Europe/London with explicit
 * locale + options. Always overrides `timeZone` to London.
 */
export function formatLondon(
    input: Date | string,
    options: Intl.DateTimeFormatOptions = {},
    locale: string | string[] = 'en-GB'
): string {
    const date = typeof input === 'string' ? new Date(input) : input;
    return new Intl.DateTimeFormat(locale, {
        ...options,
        timeZone: LONDON_TZ,
    }).format(date);
}

/** Format as `dd/mm/yyyy` (en-GB) in London. */
export function formatLondonDate(input: Date | string): string {
    return formatLondon(input, { year: 'numeric', month: '2-digit', day: '2-digit' });
}

/** Format as `HH:MM` (en-GB, 24h) in London. */
export function formatLondonTime(input: Date | string): string {
    return formatLondon(input, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Format as `dd/mm/yyyy, HH:MM` (en-GB, 24h) in London. */
export function formatLondonDateTime(input: Date | string): string {
    return formatLondon(input, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}

/** Lexical comparison of two `YYYY-MM-DD` keys: -1 / 0 / 1. */
export function compareLondonDateKeys(a: string, b: string): number {
    return a < b ? -1 : a > b ? 1 : 0;
}

/**
 * Day-of-week number (0=Sunday … 6=Saturday) for the given London date key.
 * DST-safe — operates purely in the calendar domain.
 */
export function londonDayOfWeek(dateKey: string): number {
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
